/**
 * Eastern Time formatting utilities.
 *
 * Goal: every webinar / seminar scheduled_date is displayed consistently as
 * Eastern Time (America/New_York) regardless of the viewer's location, with
 * the correct EST / EDT label based on Daylight Saving Time for that date.
 *
 * Why a single helper? Previously, the codebase used `format(new Date(x), ...)`
 * which silently converted to the browser's local timezone but then appended
 * an "EST" label, causing a Bangalore user to think a 2pm EST webinar was at
 * 11:30pm EST. This utility fixes that for good.
 */
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

export const ET_TIMEZONE = 'America/New_York';

/**
 * Format a UTC/ISO date string as Eastern Time using a date-fns format token.
 * @param {string|Date|null} dateInput
 * @param {string} formatStr - date-fns format pattern (e.g. "MMM dd, yyyy 'at' h:mm a")
 * @returns {string}
 */
export function formatET(dateInput, formatStr = "MMM dd, yyyy 'at' h:mm a") {
  if (!dateInput) return 'TBD';
  try {
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (isNaN(date.getTime())) return 'TBD';
    return formatInTimeZone(date, ET_TIMEZONE, formatStr);
  } catch {
    return 'TBD';
  }
}

/**
 * Return the timezone label for Eastern Time displays.
 *
 * Per product decision, we always display "EST" regardless of DST to avoid
 * customer confusion across emails / cards / detail pages. The actual time
 * value is still rendered in America/New_York wall-clock time so the hour
 * matches what users in NY see.
 *
 * @param {string|Date|null} _dateInput - accepted for back-compat; ignored
 * @returns {'EST'}
 */
export function getETAbbr(_dateInput) {
  return 'EST';
}

/**
 * Convert an admin form's date + time input (interpreted as ET wall-clock time)
 * to a UTC ISO string for storage in the DB.
 * Example: ('2026-06-15', '14:00') --> '2026-06-15T18:00:00.000Z'  (EDT, UTC-4)
 *          ('2026-12-15', '14:00') --> '2026-12-15T19:00:00.000Z'  (EST, UTC-5)
 */
export function etInputToISO(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  try {
    const wallClock = new Date(`${dateStr}T${timeStr}:00`);
    if (isNaN(wallClock.getTime())) return null;
    return fromZonedTime(wallClock, ET_TIMEZONE).toISOString();
  } catch {
    return null;
  }
}

// Common preset formatters
export const formatETDate = (d) => formatET(d, 'EEEE, MMMM dd, yyyy');     // Tuesday, May 20, 2026
export const formatETShortDate = (d) => formatET(d, 'MMM dd, yyyy');        // May 20, 2026
export const formatETMonthDay = (d) => formatET(d, 'EEE, MMM dd, yyyy');    // Tue, May 20, 2026
export const formatETTime = (d) => formatET(d, 'h:mm a');                   // 2:00 PM
export const formatETDateTime = (d) => formatET(d, "MMM dd, yyyy 'at' h:mm a"); // May 20, 2026 at 2:00 PM
export const formatETInputDate = (d) => formatET(d, 'yyyy-MM-dd');          // 2026-05-20 (for <input type=date>)
export const formatETInputTime = (d) => formatET(d, 'HH:mm');               // 14:00     (for <input type=time>)
